import React, { useState, useEffect } from "react";
import { calculateTrig } from "../../utils/calculations";

const TrigCalculator = ({ onSave }) => {
  const [params, setParams] = useState({
    a: "",
    b: "",
    c: "",
    angleA: 90,
    angleB: "",
    angleC: "",
  });

  const [results, setResults] = useState(null);

  useEffect(() => {
    const hasAnyInput = [params.a, params.b, params.c, params.angleB, params.angleC].some(
      (value) => value !== "" && value !== null
    );

    if (!hasAnyInput) {
      setResults(null);
      return;
    }

    const calcResults = calculateTrig(params);
    setResults(calcResults);
  }, [params]);

  const handleInputChange = (field, value) => {
    if (field === "angleA") return;

    setParams((prev) => ({
      ...prev,
      [field]: value,
      angleA: 90,
    }));
  };

  const handleCalculate = () => {
    const calcResults = calculateTrig({
      ...params,
      angleA: 90,
    });
    setResults(calcResults);
  };

  const resetForm = () => {
    setParams({
      a: "",
      b: "",
      c: "",
      angleA: 90,
      angleB: "",
      angleC: "",
    });
    setResults(null);
  };

  const copyToClipboard = () => {
    if (!results || results.errors) return;

    const text = [
      `a = ${results.a}`,
      `b = ${results.b}`,
      `c = ${results.c}`,
      `A = ${results.angleA}°`,
      `B = ${results.angleB}°`,
      `C = ${results.angleC}°`,
      `Area = ${results.area}`,
      `Perimeter = ${results.perimeter}`,
    ].join("\n");

    navigator.clipboard.writeText(text);
  };

  const handleSaveCalculation = () => {
    if (!results || results.errors || !onSave) return;

    onSave({
      summary: `Right triangle solved: a=${results.a}, b=${results.b}, c=${results.c}`,
      input: {
        a: params.a === "" ? null : params.a,
        b: params.b === "" ? null : params.b,
        c: params.c === "" ? null : params.c,
        angleA: 90,
        angleB: params.angleB === "" ? null : params.angleB,
        angleC: params.angleC === "" ? null : params.angleC,
      },
      results: {
        a: results.a,
        b: results.b,
        c: results.c,
        angleA: results.angleA,
        angleB: results.angleB,
        angleC: results.angleC,
        area: results.area,
        perimeter: results.perimeter,
      },
      warnings: results.warnings || [],
      confidence: results.confidence || "N/A",
    });
  };

  const displayValue = (field) => {
    if (field === "angleA") return 90;
    if (params[field] !== "" && params[field] !== null) return params[field];
    if (results && !results.errors && results[field] !== undefined) return results[field];
    return "";
  };

  const solvedTriangle = results && !results.errors;

  return (
    <div
      className="calculator-container"
      style={{
        background: "var(--bg-surface)",
        color: "var(--text-secondary)",
        border: "1px solid var(--border-color)",
        borderRadius: "12px",
        padding: "24px",
      }}
    >
      <div className="calculator-header" style={{ marginBottom: "24px" }}>
        <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
          📐 Right Triangle Trig Calculator
        </h1>
        <p style={{ color: "var(--text-muted)", margin: 0 }}>
          Angle A is fixed at 90°. Enter any valid right-triangle combination to solve the full triangle.
        </p>
      </div>

      <div
        className="calculator-content"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(340px, 1fr) minmax(340px, 1fr)",
          gap: "24px",
          alignItems: "start",
        }}
      >
        <div
          className="calculator-form"
          style={{
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "20px",
          }}
        >
          <div className="form-section" style={{ marginBottom: "24px" }}>
            <h3
              style={{
                color: "var(--text-primary)",
                marginTop: 0,
                marginBottom: "16px",
              }}
            >
              Triangle Inputs
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "14px",
              }}
            >
              <div className="form-group">
                <label>Side a</label>
                <input
                  type="number"
                  step="any"
                  value={displayValue("a")}
                  onChange={(e) => handleInputChange("a", e.target.value)}
                  placeholder="Hypotenuse"
                />
              </div>

              <div className="form-group">
                <label>Side b</label>
                <input
                  type="number"
                  step="any"
                  value={displayValue("b")}
                  onChange={(e) => handleInputChange("b", e.target.value)}
                  placeholder="Leg b"
                />
              </div>

              <div className="form-group">
                <label>Side c</label>
                <input
                  type="number"
                  step="any"
                  value={displayValue("c")}
                  onChange={(e) => handleInputChange("c", e.target.value)}
                  placeholder="Leg c"
                />
              </div>

              <div className="form-group">
                <label>Angle A (°)</label>
                <input
                  type="number"
                  step="any"
                  value={90}
                  readOnly
                  disabled
                  style={{
                    opacity: 0.75,
                    cursor: "not-allowed",
                  }}
                />
              </div>

              <div className="form-group">
                <label>Angle B (°)</label>
                <input
                  type="number"
                  step="any"
                  value={displayValue("angleB")}
                  onChange={(e) => handleInputChange("angleB", e.target.value)}
                  placeholder="Angle B"
                />
              </div>

              <div className="form-group">
                <label>Angle C (°)</label>
                <input
                  type="number"
                  step="any"
                  value={displayValue("angleC")}
                  onChange={(e) => handleInputChange("angleC", e.target.value)}
                  placeholder="Angle C"
                />
              </div>
            </div>
          </div>

          <div
            className="form-actions"
            style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
          >
            <button onClick={handleCalculate} className="btn-primary" type="button">
              Calculate
            </button>
            <button onClick={resetForm} className="btn-secondary" type="button">
              Reset
            </button>
          </div>

          <div
            style={{
              marginTop: "20px",
              padding: "14px",
              background: "var(--bg-surface)",
              border: "1px solid var(--border-color)",
              borderRadius: "10px",
            }}
          >
            <h4
              style={{
                marginTop: 0,
                marginBottom: "10px",
                color: "var(--text-primary)",
              }}
            >
              How to use
            </h4>
            <p style={{ margin: 0, color: "var(--text-secondary)" }}>
              This solver is for right triangles only. Angle A is always 90°.
              Enter any valid combination such as a+b, a+c, b+c, a+angle, b+angle,
              or c+angle. Solved values populate on the diagram and in the result cards.
            </p>
          </div>
        </div>

        <div
          className="calculator-results"
          style={{
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "20px",
            minHeight: "100%",
            overflow: "hidden",
          }}
        >
          <div
            className="results-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "20px",
            }}
          >
            <h3 style={{ color: "var(--text-primary)", margin: 0 }}>
              Triangle Diagram
            </h3>

            {results && (
              <div
                className="confidence-indicator"
                style={{ color: "var(--text-secondary)" }}
              >
                Confidence:{" "}
                <span
                  className={`confidence-${results.confidence.toLowerCase()}`}
                  style={{ color: "var(--accent)", fontWeight: 700 }}
                >
                  {results.confidence}
                </span>
              </div>
            )}
          </div>

          <div
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-color)",
              borderRadius: "12px",
              padding: "12px",
              marginBottom: "20px",
              width: "100%",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "100%",
                aspectRatio: "4 / 3",
                margin: "0 auto",
              }}
            >
              <svg
                viewBox="0 0 320 240"
                preserveAspectRatio="xMidYMid meet"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                }}
              >
                <polygon
                  points="82,188 258,188 82,62"
                  fill="rgba(255,255,255,0.02)"
                  stroke="var(--accent)"
                  strokeWidth="3"
                />

                <rect
                  x="82"
                  y="172"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="var(--text-muted)"
                  strokeWidth="2"
                />


                <text
                  x="170"
                  y="220"
                  fill="var(--text-primary)"
                  fontSize="16"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  c = {solvedTriangle ? results.c : "—"}
                </text>

                <text
                  x="24"
                  y="126"
                  fill="var(--text-primary)"
                  fontSize="16"
                  fontWeight="700"
                  textAnchor="start"
                >
                  b = {solvedTriangle ? results.b : "—"}
                </text>

                <text
                  x="214"
                  y="106"
                  fill="var(--text-primary)"
                  fontSize="16"
                  fontWeight="700"
                  textAnchor="middle"
                  transform="rotate(-35 194 106)"
                >
                  a = {solvedTriangle ? results.a : "—"}
                </text>

                <text
                  x="102"
                  y="166"
                  fill="var(--accent)"
                  fontSize="14"
                  fontWeight="700"
                >
                  90°
                </text>

                <text
                  x="238"
                  y="170"
                  fill="var(--accent)"
                  fontSize="14"
                  fontWeight="700"
                >
                  {solvedTriangle ? `${results.angleB}°` : "B"}
                </text>

                <text
                  x="88"
                  y="60"
                  fill="var(--accent)"
                  fontSize="14"
                  fontWeight="700"
                >
                  {solvedTriangle ? `${results.angleC}°` : "C"}
                </text>
              </svg>
            </div>
          </div>

          {results?.errors && (
            <div
              className="warnings"
              style={{
                background: "rgba(239, 68, 68, 0.12)",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                borderRadius: "10px",
                padding: "16px",
                marginBottom: "20px",
              }}
            >
              <h4
                style={{
                  color: "var(--text-primary)",
                  marginTop: 0,
                  marginBottom: "10px",
                }}
              >
                ⚠️ Input Issue
              </h4>
              <ul
                style={{
                  paddingLeft: "20px",
                  color: "var(--text-secondary)",
                  margin: 0,
                }}
              >
                {Object.values(results.errors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {solvedTriangle && (
            <>
              <div
                className="results-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                <ResultCard label="Side a" value={results.a} />
                <ResultCard label="Side b" value={results.b} />
                <ResultCard label="Side c" value={results.c} />
                <ResultCard label="Angle A" value="90°" />
                <ResultCard label="Angle B" value={`${results.angleB}°`} />
                <ResultCard label="Angle C" value={`${results.angleC}°`} />
                <ResultCard label="Area" value={results.area} />
                <ResultCard label="Perimeter" value={results.perimeter} />
              </div>

              {results.warnings?.length > 0 && (
                <div
                  className="warnings"
                  style={{
                    background: "rgba(245, 158, 11, 0.12)",
                    border: "1px solid rgba(245, 158, 11, 0.35)",
                    borderRadius: "10px",
                    padding: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <h4
                    style={{
                      color: "var(--text-primary)",
                      marginTop: 0,
                      marginBottom: "10px",
                    }}
                  >
                    ⚠️ Warnings
                  </h4>
                  <ul
                    style={{
                      paddingLeft: "20px",
                      color: "var(--text-secondary)",
                      margin: 0,
                    }}
                  >
                    {results.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div
                className="results-actions"
                style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
              >
                <button
                  onClick={handleSaveCalculation}
                  className="btn-primary"
                  type="button"
                >
                  💾 Save Calculation
                </button>
                <button
                  onClick={copyToClipboard}
                  className="btn-secondary"
                  type="button"
                >
                  📄 Copy
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ResultCard = ({ label, value }) => {
  return (
    <div
      className="result-item"
      style={{
        background: "var(--bg-surface)",
        border: "1px solid var(--border-color)",
        borderRadius: "10px",
        padding: "14px",
      }}
    >
      <label
        style={{
          display: "block",
          color: "var(--text-muted)",
          fontSize: "14px",
          marginBottom: "6px",
        }}
      >
        {label}
      </label>
      <span
        style={{
          color: "var(--text-primary)",
          fontWeight: 700,
          fontSize: "18px",
        }}
      >
        {value}
      </span>
    </div>
  );
};

export default TrigCalculator;